import { useDeferredValue } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import classes from './MarkdownText.module.css';
import { markdownTableComponents } from '#plugin-chat/lib/ui/markdown-table-components.tsx';

// Static configuration hoisted to module scope so that consumers (and
// react-markdown's internal child cache) see referentially stable values
// across re-renders. Recreating these per render — especially during
// 5-10 Hz streaming — defeats memoization downstream.
const remarkPlugins = [remarkGfm];

interface MarkdownTextProps {
  /** Markdown source. Pass through assistant-ui's `Text` component override (`AssistantBubble`). */
  text: string;
}

/**
 * Renders markdown content with Mantine-styled headings, tables, and dividers.
 *
 * The incoming `text` is wrapped with {@link useDeferredValue} so that the
 * markdown parse + reconcile pipeline runs at React's transition priority.
 * During streaming (5-10 Hz chunk arrival), React drops intermediate states
 * — the bubble re-renders cheaply on every chunk, while the deferred subtree
 * skips re-tokenization for chunks that land while the previous parse is
 * still committing.
 *
 * Renders via `react-markdown` directly (rather than assistant-ui's
 * `MarkdownTextPrimitive`) because the text is already passed in as a prop
 * — there's no need for the primitive's context-binding here.
 */
export function MarkdownText({ text }: MarkdownTextProps) {
  const deferredText = useDeferredValue(text);
  return (
    <div className={classes.markdownText}>
      <Markdown components={markdownTableComponents} remarkPlugins={remarkPlugins}>
        {deferredText}
      </Markdown>
    </div>
  );
}
